import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { map, takeUntil, tap } from 'rxjs';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { finalize } from 'rxjs/operators';
import {
  CreateFormBuilderCommand,
  CustomResultOfListOfGetFormBuilderFieldsListDto,
  FormBuilderEntityFieldModel,
  GetEnumValuesDto,
  GetFormBuilderFieldsListDto,
  UpdateFormBuilderCommand,
} from 'src/app/core/api/models';
import {
  FormBuilderFieldsService,
  FormBuildersService,
} from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { EnumToTextConvertorPipe } from 'src/app/core/pipes/enum-text.pipe';
import { AppConfigService } from 'src/app/core/services/config.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { BaseFormModule } from 'src/app/pages/shared/base/modules/base-form.module';
import { AppNgxEditorComponent } from 'src/app/shared/components/text-editor/app-ngx-editor/app-ngx-editor.component';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';
import { FieldsManagementFormModalComponent } from '../../fields-management/fields-management-form/fields-management-form-modal/fields-management-form-modal.component';
import { CreateFieldDialogComponent } from '../create-field-dialog/create-field-dialog.component';
import { FieldsComponent } from './fields/fields.component';
import { FormsManagementAppearanceSettingComponent } from './forms-management-appearance-setting/forms-management-appearance-setting.component';
import { FormsManagementPreviewModalComponent } from './forms-management-preview-modal/forms-management-preview-modal.component';

@Component({
  selector: 'app-forms-management-form',
  templateUrl: './forms-management-form.component.html',
  styleUrls: ['./forms-management-form.component.scss'],
  imports: [
    BaseFormModule,
    FieldsComponent,
    AppNgxEditorComponent,
    CdkDropList,
    CdkDrag,
    FormsManagementAppearanceSettingComponent,
  ],
})
export class FormsManagementFormComponent
  extends BaseFormPageComponent
  implements OnInit
{
  //#region Properties

  model: any = {};
  imageUrl: any;
  activeFieldsetId: number = 0;
  activeField: any = {};
  formBuilderFieldsList: Array<GetFormBuilderFieldsListDto> | null;
  formBuilderFieldTypesEnumValues: GetEnumValuesDto[] = [];
  formBuilderThemesEnumValues: GetEnumValuesDto[] = [];
  innerWidth: number;
  imageLoading: boolean = false;

  @ViewChild('fieldElement') fieldElement: ElementRef;
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerWidth = window.innerWidth;
  }

  //#endregion

  constructor(
    private currentService: FormBuildersService,
    protected formBuilderFieldsService: FormBuilderFieldsService,
    private enumToTextConvertorPipe: EnumToTextConvertorPipe,
    protected dialogService: DialogService
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.sortFieldsList();
    this.getEnumsValues();
    this.innerWidth = window.innerWidth;
  }

  ngAfterViewInit(): void {
    this.getItemsApi();
  }

  getEnumsValues() {
    this.getEnumValuesApi('FormBuilderFieldTypes')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => {
        this.formBuilderFieldTypesEnumValues = values;
      });

    this.getEnumValuesApi('FormBuilderThemes')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => {
        this.formBuilderThemesEnumValues = values;
      });
  }

  //#region init methods

  initForm() {
    this.form = new FormGroup({
      title: new FormControl(''),
      description: new FormControl(''),
      backgroundImage: new FormControl(''),
      theme: new FormControl(''),
      fields: new FormControl(''),
    });
  }

  initModel(): void {
    try {
      super.initModel();
      if (!this.model.fields) this.model.fields = [];
      if (this.formPageConfig.isEditMode && this.model.backgroundImagePath)
        this.imageUrl =
          AppConfigService.config.apiUrl + '/' + this.model.backgroundImagePath;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.whiteMessages
      );
    }
  }

  baseObserverSelectSearch = {
    next: (items) => {
      if (items?.data) this.formBuilderFieldsList = items?.data;
      else this.formBuilderFieldsList = items;
    },
  };

  //#region Api
  getItemsApi() {
    this.formBuilderFieldsService
      .formBuilderFieldsGetFormBuilderFieldsList()
      .pipe(
        this.basePipeOperators(),
        map((items: CustomResultOfListOfGetFormBuilderFieldsListDto) => {
          return items?.data?.map((item) => {
            let translatedFieldTypes = '';
            translatedFieldTypes = this.enumToTextConvertorPipe.transform(
              item?.fieldType,
              this.formBuilderFieldTypesEnumValues
            );

            return {
              ...item,
              optionTitle: `  ${item?.isRequired ? ' * ' : ''} ${
                item?.title
              } (${translatedFieldTypes})  `,
            };
          });
        })
      )
      .subscribe(this.baseObserverSelectSearch);
  }

  //Create
  baseCreateApi(model: CreateFormBuilderCommand) {
    this.unSubs();

    this.loading = true;
    model.description = this.form.get('description')?.value;

    this.currentService
      .formBuildersCreate({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //Update
  baseUpdateApi(model: UpdateFormBuilderCommand) {
    this.unSubs();

    this.loading = true;
    model.description = this.form.get('description')?.value;

    this.currentService
      .formBuildersUpdatePut({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //#endregion

  //#region field

  onRemoveField(id: number) {
    try {
      this.model.fields = this.model.fields.filter(
        (item) => item.formBuilderFieldId !== id
      );
      this.setActive(0, this.model?.fields[this.model?.fields?.length - 1]);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.whiteMessages
      );
    }
  }

  setFields(event: FormBuilderEntityFieldModel) {
    try {
      this.model.fields.forEach((item) => {
        if (item.formBuilderFieldId === this.activeFieldsetId)
          Object.assign(item, event);
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.whiteMessages
      );
    }
  }

  setActive(id: number, field: FormBuilderEntityFieldModel) {
    this.activeFieldsetId = field.formBuilderFieldId;
    this.activeField = field;
    this.model.formBuilderFieldId = field.formBuilderFieldId;

    if (this.innerWidth < 1024)
      this.fieldElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
  }

  //#endregion

  //#region file 📁

  getFile(file) {
    try {
      if (!file) {
        this.imageUrl = '';
        this.model.backgroundImagePath = '';
        return;
      }
      this.imageLoading = true;
      this.unSubs();
      this.currentService
        .formBuildersUploadBackgroundImage({ body: { BackgroundImage: file } })
        .pipe(
          finalize(() => {
            this.loading = false;
            this.pageConfig.loading = false;
            this.imageLoading = false;
          }),
          takeUntil(this.onDestroy$)
        )
        .subscribe((imageName) => {
          this.model.backgroundImagePath = imageName.data;
          this.imageLoading = false;
          this.onFileChanged(file);
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.whiteMessages
      );
    }
  }

  onFileChanged(file: any) {
    try {
      if (file) {
        this.form.controls['backgroundImage'].setErrors(null);

        var reader = new FileReader();
        reader.onload = (event: any) => {
          this.imageUrl = event.target.result;
        };

        reader.readAsDataURL(file);
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.whiteMessages
      );
    }
  }

  //#endregion

  //#region sort

  // update Order
  updateOrder() {
    this.model.fields.forEach((column, index) => {
      column.order = index + 1;
    });
  }

  // sort fields list
  sortFieldsList() {
    if (!this.model?.fields.length) return;

    this.model.fields.sort((a, b) => {
      const valueA = a['order'];
      const valueB = b['order'];
      return valueA - valueB;
    });

    this.activeField = this.model.fields[0];
    this.activeFieldsetId = this.model.fields[0].formBuilderFieldId;
  }

  //#endregion

  // dreg and drop
  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.model.fields, event.previousIndex, event.currentIndex);
    this.updateOrder();
  }

  //#region dialog
  openFieldsManagementFormDialog() {
    const modalResult = this.dialogService.openModal({
      component: FieldsManagementFormModalComponent,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getItemsApi();
        }
      });
  }

  openCreateNewFieldDialog() {
    let data = {
      formBuilderFieldsList: this.formBuilderFieldsList,
      fields: this.model.fields,
    };
    const modalResult = this.dialogService.openModal({
      component: CreateFieldDialogComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.fields.push(res);
          this.setActive(1, res);
        }
      });
  }

  //  form preview
  previewPage() {
    let data = {
      previewData: this.model,
    };

    const modalResult = this.dialogService.openModal({
      component: FormsManagementPreviewModalComponent,
      data: data,
    });
    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe();
  }
  //#endregion
}
